import {Component, OnDestroy, OnInit} from '@angular/core';
import {ListView, TypeComparaison, TypeFilter} from "@domain/common/list-view";
import {TranslateService} from '@ngx-translate/core';
import {Router, ActivatedRoute} from "@angular/router";
import {environment} from '@environments/environment';
import {Sorting} from '@domain/common/list-view/sorting';
import {Contrat} from "@domain/contrat/contrat";
import {BehaviorSubject, Subscription} from "rxjs";
import {FloatingButtonAction, TypeAction} from '@share/component/floating-button/floating-button';
import {FacturesFournisseursContratItemComponent} from "./factures-fournisseurs-contrat-item.component";
import {TypeOnglet} from "../fournisseurs-infos/factures-fournisseurs-infos.component";
import {FournisseursTabsService} from '@services/admin/facture/fournisseurs.tabs.service';

/**
 * Onglet contrats fournisseurs
 *
 * @author Yannick Gauthier
 * @date 14/08/2025
 */
@Component({
    selector: 'factures-fournisseurs-contrat',
    host: {'data-test-id': 'factures-fournisseurs-contrat'},
    templateUrl: './factures-fournisseurs-contrat.component.html'
})
export class FacturesFournisseursContratComponent implements OnInit, OnDestroy {
    /** Liste view des contrats */
    contratListView: ListView<Contrat, FacturesFournisseursContratItemComponent>;

    /** ID du fournisseur */
    idFournisseur: number;

    /** Liste des actions disponibles */
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

    /** Souscription à l'onglet actif */
    private activeTabSubscription: Subscription;

    /**
     * Constructeur
     *
     * @param translateService Service de traduction
     * @param router Service de routage
     * @param activatedRoute Route activée
     * @param fournisseursTabsService Service de gestion des onglets
     */
    constructor(
        public translateService: TranslateService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private fournisseursTabsService: FournisseursTabsService
    ) {
    }

    /**
     * Initialisation
     */
    ngOnInit(): void {
        //Récupération de l'id du fournisseur dans l'URL
        this.idFournisseur = +this.activatedRoute.snapshot.params.id;

        //Initialisation de la liste view
        this.contratListView = new ListView<Contrat, FacturesFournisseursContratItemComponent>({
            component: FacturesFournisseursContratItemComponent,
            uri: `${environment.baseUrl}/controller/Contrat/listeContratByFournisseur/${this.idFournisseur}`,
            title: this.translateService.instant('admin.frais.fournisseurs.contrat.title'),
            defaultOrder: 'libelle',
            listeFilters: [{
                clef: 'libelle',
                title: this.translateService.instant('admin.frais.fournisseurs.contrat.filtres.libelle'),
                type: TypeFilter[TypeFilter.STRING],
                typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                isDefault: true
            }, {
                clef: 'numero',
                title: this.translateService.instant('admin.frais.fournisseurs.contrat.numero'),
                type: TypeFilter[TypeFilter.STRING],
                typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                isDefault: true
            }, {
                clef: 'dateFin',
                title: this.translateService.instant('admin.frais.fournisseurs.contrat.dateFin'),
                type: TypeFilter[TypeFilter.DATE],
                typeComparaison: TypeComparaison[TypeComparaison.BETWEEN]
            }],
            isFilter: true
        });

        //Définition des colonnes de tri
        this.contratListView.columns = [
            {key: 'libelle', title: this.translateService.instant('admin.frais.fournisseurs.contrat.filtres.libelle')},
            {key: 'numero', title: this.translateService.instant('admin.frais.fournisseurs.contrat.numero')},
            {key: 'dateFin', title: this.translateService.instant('admin.frais.fournisseurs.contrat.dateFin')}
        ];

        //Ajout du tri de la liste selon l'ordre voulu
        this.contratListView.sorting = new Sorting(
            this.contratListView.columns,
            'libelle'
        );

        //Nom de la classe
        this.contratListView.className = "factures-fournisseurs-contrat";

        //Persistence des filtres
        this.contratListView.isPersistFilters = true;

        //Définition de la liste des actions possibles
        const actions = [
            {
                type: TypeAction.PRIMARY,
                icone: 'nio icon-ajouter',
                libelle: 'admin.frais.fournisseurs.contrat.action.ajouter',
                doAction: () => this.creerContrat()
            }
        ];
        
        this.listeActions.next(actions);
        
        //Mettre à jour les actions dans le service si l'onglet actif est CONTRAT
        if (this.fournisseursTabsService.getActiveTabCode() === TypeOnglet.CONTRAT) {
            this.fournisseursTabsService.setActiveTabActions(actions);
        }
        
        //S'abonner aux changements d'onglet actif
        this.activeTabSubscription = this.fournisseursTabsService.getActiveTabCode$().subscribe(tabCode => {
            if (tabCode === TypeOnglet.CONTRAT) {
                //Si l'onglet CONTRAT devient actif, mettre à jour les actions
                this.fournisseursTabsService.setActiveTabActions(this.listeActions.value);
            }
        });
    }

    /**
     * Destruction
     */
    ngOnDestroy() {
        //Destruction de l'abonnement
        this.activeTabSubscription.unsubscribe();
    }

    /**
     * Méthode appelée lors de la création d'un nouveau contrat
     */
    creerContrat(): void {
        //Récupération de l'ID du fournisseur depuis la route parente
        const idFournisseur = this.activatedRoute.parent?.snapshot.params['id'];

        this.router.navigate(['Admin/Frais/Factures/Contrat', 0], {
            queryParams: { idFournisseur: idFournisseur }
        });
    }
}